import { Button } from "@/components/ui/button";
import { Search, Home, BadgeCheck, Star, ArrowRight, MessageCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SignupModal } from "./SignupModal";
import { useNavigate, Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
// Hero image moved to public folder for LCP optimization
const heroPlazaImage = "/images/hero-plaza.webp";

interface HeroSectionProps {
  onGeneralSignup?: () => void;
}

export const HeroSection = ({ onGeneralSignup }: HeroSectionProps) => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handlePublish = () => {
    navigate('/iscrizione-proprietario');
  };

  const handleSearch = () => {
    navigate('/cerca-annunci');
  };

  return (
    <section className="relative bg-gradient-hero text-primary-foreground overflow-hidden">
      {/* Background con overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto max-w-6xl py-12 md:py-20 px-4">
        
        {/* Layout Split: Mobile stack, Desktop side-by-side */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12">
          
          {/* Left Side - Claim & CTAs */}
          <div className="flex-1 text-center lg:text-left mb-10 lg:mb-0">
            
            {/* H1 Claim */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight drop-shadow-lg">
              {t('heroCta.headline')}{" "}
              <span className="text-accent">{t('heroCta.headlineAccent')}</span> {t('heroCta.headlineEnd')}
            </h1>
            
            {/* Sottoclaim micro-urgenza */}
            <p className="text-base sm:text-lg md:text-xl mb-6 text-primary-foreground/90 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              {t('heroCta.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
              <div className="flex flex-col items-center sm:items-start">
                <Button 
                  onClick={handlePublish}
                  size="lg"
                  className="h-14 px-8 text-base font-bold bg-accent hover:bg-accent/90 text-accent-foreground shadow-xl transition-all duration-300 hover:scale-105"
                >
                  📢 {t('heroCta.publishButton')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <span className="text-xs text-primary-foreground/70 mt-1.5 text-center sm:text-left max-w-[280px]">
                  {t('heroCta.publishSubtext')}
                </span>
              </div>
              
              <div className="flex flex-col items-center sm:items-start">
                <Button 
                  onClick={handleSearch}
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-base font-semibold bg-white/10 border-2 border-white/40 text-primary-foreground hover:bg-white/20 hover:border-white/60 transition-all duration-300"
                >
                  <Search className="mr-2 h-5 w-5" />
                  {t('userPath.renterCtaShort')}
                </Button>
                <span className="text-xs text-primary-foreground/70 mt-1.5 text-center sm:text-left max-w-[280px]">
                  {t('heroCta.searchSubtext')}
                </span>
              </div>
            </div>

            {/* Paola CTA - richiamo assistente AI */}
            <button
              onClick={() => navigate('?chat=paola')}
              className="group inline-flex items-center gap-3 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/30 hover:border-white/50 rounded-full pl-2 pr-5 py-2 mb-6 transition-all duration-300 hover:scale-[1.02] shadow-lg"
              aria-label="Chiedi a Paola, assistente virtuale"
            >
              <span className="relative flex h-9 w-9 shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
                <img src="/paola-avatar.jpg" alt="Paola" className="relative h-9 w-9 rounded-full object-cover ring-2 ring-white/60" />
              </span>
              <span className="text-sm sm:text-base font-medium text-primary-foreground text-left">
                <span className="opacity-80">{isEnglish ? "Here to help?" : "Posso aiutarti?"}</span>{" "}
                <span className="font-semibold">{isEnglish ? "Ask Paola" : "Chiedi a Paola"}</span>
              </span>
              <ArrowRight className="h-4 w-4 text-primary-foreground/80 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Social Proof evergreen - Variante A Minimale Elegante */}
            <div className="inline-flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-2 sm:gap-4 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20 shadow-lg animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-5 w-5 text-accent" />
                <span className="text-sm font-medium text-primary-foreground">{t('heroCta.socialProof1')}</span>
              </div>
              <span className="hidden sm:inline text-primary-foreground/40">•</span>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-accent fill-accent" />
                <span className="text-sm font-medium text-primary-foreground">{t('heroCta.socialProof2')}</span>
              </div>
            </div>
          </div>

          {/* Right Side - Image with Badge */}
          {/* pb-10 reserves space for the absolutely positioned card that extends -bottom-4 */}
          <div className="flex-1 max-w-md lg:max-w-lg mx-auto lg:mx-0 pb-10">
            <div className="relative" style={{ minHeight: '320px' }}>
              {/* Image Container */}
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white/20">
                <div className="relative aspect-[4/3]">
                  <img 
                    src={heroPlazaImage} 
                    alt={t('heroCta.imageAlt')}
                    className="w-full h-full object-cover"
                    width={500}
                    height={375}
                    loading="eager"
                    fetchPriority="high"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>
              </div>
              
              {/* Badge Verificato */}
              <div className="absolute -top-3 -right-3 sm:top-4 sm:right-4 bg-white text-primary px-4 py-2 rounded-full shadow-xl flex items-center gap-2 font-semibold text-sm border-2 border-accent">
                <BadgeCheck className="h-5 w-5 text-accent" />
                {t('heroCta.verifiedBadge')}
              </div>

            </div>
          </div>
        </div>
      </div>
      
      <SignupModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};
